import React from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const HeroSection = ({ onBookingClick }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.8]);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Carrusel de imágenes de fondo
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const backgroundImages = [
    'https://customer-assets.emergentagent.com/job_headspace-admin/artifacts/vpg7bm03_Imagen%20de%20WhatsApp%202025-05-06%20a%20las%2021.03.24_e6a314bc.jpg',
    'https://customer-assets.emergentagent.com/job_headspace-admin/artifacts/6j86dg1u_maumaU.jpg'
  ];

  // Carrusel de frases
  const [currentPhraseIndex, setCurrentPhraseIndex] = React.useState(0);
  const phrases = [
    "Donde la estética, armonía y relax se encuentran",
    "Tu espacio de bienestar y renovación",
    "Un momento para reconectar contigo"
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 6000); // Cambiar cada 6 segundos

    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 4000); // Cambiar cada 4 segundos

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Animated gradient background */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-br from-black via-[#1a1a1a] to-[#2d2d2d]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
      />

      {/* Parallax background images con transición */}
      <motion.div
        className="absolute inset-0"
        style={{ y }}
      >
        {backgroundImages.map((image, index) => (
          <motion.div
            key={image}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: currentImageIndex === index ? 1 : 0,
            }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
          >
            <div 
              className="w-full h-full bg-cover bg-center opacity-30"
              style={{
                backgroundImage: `url('${image}')`,
                backgroundPosition: 'center 40%' // Centrar mejor en desktop
              }}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Animated overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/60" />

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[#C9A86A] rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <motion.div
        ref={ref}
        className="relative z-10 text-center px-6 max-w-5xl mx-auto"
        style={{ opacity, scale }}
      >
        {/* Logo */}
        <motion.img
          src="https://customer-assets.emergentagent.com/job_793a21d1-f278-4746-b089-be151b084274/artifacts/gzv2mp07_LogoMauma.png"
          alt="MAUMA"
          className="w-48 sm:w-64 md:w-80 mx-auto mb-6 md:mb-8"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            duration: 0.8,
            ease: "easeOut"
          }}
        />

        {/* Title */}
        <motion.h1
          className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-light text-white mb-3 md:mb-4 px-4"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <span className="font-serif italic text-[#C9A86A]">
            Beauty House
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-lg sm:text-xl md:text-2xl text-[#F5F3EE]/90 mb-4 md:mb-8 font-light tracking-wide px-4"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          Head Spa en Carrasco
        </motion.p>

        {/* Frases alternadas con animación */}
        <div className="relative h-16 sm:h-20 mb-8 md:mb-12 px-4 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentPhraseIndex}
              className="absolute inset-0 flex items-center justify-center text-base sm:text-lg md:text-xl text-[#F5F3EE]/70 font-light italic"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ 
                duration: 0.8,
                ease: "easeInOut"
              }}
            >
              {phrases[currentPhraseIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* CTA Button */}
        <motion.button
          onClick={onBookingClick}
          className="group relative px-8 sm:px-10 py-3 sm:py-4 bg-[#C9A86A] text-black font-medium text-base sm:text-lg rounded-full overflow-hidden shadow-2xl mx-4"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          whileHover={{ scale: 1.05, boxShadow: "0 20px 60px rgba(201, 168, 106, 0.4)" }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="relative z-10">Reservar mi Experiencia</span>
          
          {/* Hover effect */}
          <motion.div
            className="absolute inset-0 bg-[#d4b887]"
            initial={{ x: '-100%' }}
            whileHover={{ x: 0 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="flex flex-col items-center gap-2 cursor-pointer"
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-[#C9A86A] text-sm font-light tracking-widest">DESCUBRE</span>
            <svg
              className="w-6 h-6 text-[#C9A86A]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
